// ============================================
// BLOKAS 1: IMPORTAI
// ============================================
console.log('[useMarketPrices.ts] BLOKAS 1: Importuojamos priklausomybės');
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';

// ============================================
// BLOKAS 2: INTERFACE APIBRĖŽIMAI
// ============================================
console.log('[useMarketPrices.ts] BLOKAS 2: Apibrėžiami tipai');
interface MarketPricesResult {
  prices: Record<string, number>;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
}

interface MarketPricesResponse {
  prices: Record<string, number>;
  errors?: Record<string, string>;
  timestamp: string;
  exchange: string;
  success: boolean;
  stats?: {
    total: number;
    successful: number;
    failed: number;
  };
}

// ============================================
// BLOKAS 3: USE_MARKET_PRICES HOOK'AS
// ============================================
console.log('[useMarketPrices.ts] BLOKAS 3: Sukuriamas useMarketPrices hook');
/**
 * Custom hook to fetch and manage real-time market prices for selected coins
 * @param coinSymbols Array of coin symbols (e.g., ['BTC', 'ETH'])
 * @param quoteCurrency Quote currency (e.g., 'USDT') - DINAMINĖ: iš kliento pasirinkimo
 * @param exchange Exchange name - DINAMINĖ: iš kliento pasirinkimo
 * @param pollInterval Polling interval in milliseconds - DINAMINĖ: iš kliento pasirinkimo
 * @returns Object containing prices, loading state, error state, and refetch function
 */
export const useMarketPrices = (
  coinSymbols: string[],
  quoteCurrency: string,
  exchange: string = '', // DINAMINĖ: tuščia, jei nėra duomenų
  pollInterval: number = 10000
): MarketPricesResult => {
  console.log('[useMarketPrices.ts] SUB-BLOKAS 3.1: Hook inicijavimas su parametrais:', {
    coinSymbols,
    quoteCurrency,
    exchange,
    pollInterval
  });
  
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Use refs to track the current values for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef<boolean>(true);

  console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2: Sukuriama fetchMarketPrices funkcija');
  const fetchMarketPrices = async () => {
    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.1: fetchMarketPrices pradžia');
    
    // Skip if no coins selected
    if (!coinSymbols || coinSymbols.length === 0) {
      console.log('🔍 [useMarketPrices.ts] No coins selected, clearing prices');
      setPrices({});
      setLoading(false);
      setError(null);
      setLastUpdated(null);
      return;
    }

    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.2: Tikrinama quoteCurrency');
    // Skip if no quote currency
    if (!quoteCurrency) {
      console.log('🔍 [useMarketPrices.ts] No quote currency provided, clearing prices');
      console.warn('⚠️ [useMarketPrices.ts] WARNING: quoteCurrency yra tuščias!');
      console.warn('⚠️ [useMarketPrices.ts] Tai gali sukelti klaidą: "Blogas simbolio formatas"');
      
      setPrices({});
      setLoading(false);
      setError(null);
      setLastUpdated(null);
      return;
    }

    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.3: Tikrinami coin simboliai');
    // Validate coin symbols before making request
    const validCoins = coinSymbols.filter(coin => {
      if (!coin || typeof coin !== 'string') return false;
      const trimmed = coin.trim().toUpperCase();
      return /^[A-Z]{2,10}$/.test(trimmed);
    });

    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.4: Validacijos rezultatai', {
      originalCoins: coinSymbols,
      validCoins: validCoins,
      validCoinsCount: validCoins.length
    });

    if (validCoins.length === 0) {
      console.log('🔍 [useMarketPrices.ts] No valid coins after filtering');
      setPrices({});
      setLoading(false);
      setError('No valid coin symbols provided');
      setLastUpdated(null);
      return;
    }

    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.5: Tikrinama exchange');
    // Validate exchange - DINAMINĖ: priimti bet kokį exchange iš frontendo
    const validExchange = exchange || 'binance';
    console.log(`[useMarketPrices.ts] Selected exchange: ${validExchange}`);

    setLoading(true);
    setError(null);

    try {
      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.6: Paruošiamas užklausos kūnas');
      // Send coins and quoteCurrency separately - let Edge Function construct symbols
      console.log(`🚀 [useMarketPrices.ts] Input coins:`, validCoins);
      console.log(`🚀 [useMarketPrices.ts] Quote currency:`, quoteCurrency);
      console.log(`🚀 [useMarketPrices.ts] Exchange:`, validExchange);
      
      const requestBody = {
        coins: validCoins,
        quoteCurrency, // DINAMINĖ: iš kliento pasirinkimo
        exchange: validExchange // DINAMINĖ: iš kliento pasirinkimo
      };
      
      console.log(`📤 [useMarketPrices.ts] Request body:`, JSON.stringify(requestBody, null, 2));

      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.7: Siunčiama užklausa į Edge Function');
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-market-prices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log(`📡 [useMarketPrices.ts] Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        console.error('[useMarketPrices.ts] SUB-BLOKAS 3.2.8: HTTP klaida');
        const errorData = await response.json();
        console.error(`❌ [useMarketPrices.ts] HTTP error response:`, JSON.stringify(errorData, null, 2));
        throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
      }

      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.9: Apdorojamas sėkmingas atsakymas');
      const data: MarketPricesResponse = await response.json();
      console.log(`📊 [useMarketPrices.ts] Full response:`, JSON.stringify(data, null, 2));

      if (!data.prices || typeof data.prices !== 'object') {
        console.error(`❌ [useMarketPrices.ts] Invalid prices object:`, data.prices);
        throw new Error('No price data received from server - prices field is missing or invalid');
      }
      
      const pricesCount = Object.keys(data.prices).length;
      console.log(`📊 [useMarketPrices.ts] Received ${pricesCount} prices`);
      
      // Check if we have no successful prices AND the server reported failure
      if (pricesCount === 0 && !data.success) {
        console.error(`❌ [useMarketPrices.ts] Empty prices object received`);
        if (data.errors && Object.keys(data.errors).length > 0) {
          const errorMessages = Object.values(data.errors).join(', ');
          throw new Error(`No prices found for selected coins. Errors: ${errorMessages}`);
        } else {
          throw new Error(data.error || 'No prices found for selected coins. Please check if the trading pairs are supported on Binance Spot.');
        }
      }

      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.10: Konvertuojami simboliai į coin prices');
      // Convert symbol-based prices to coin-based prices
      // e.g., { "BTCUSDT": 50000 } -> { "BTC": 50000 }
      const coinPrices: Record<string, number> = {};
      const normalizedQuoteCurrency = quoteCurrency.toUpperCase();
      
      Object.entries(data.prices).forEach(([symbol, price]) => {
        // Extract coin from symbol (e.g., BTCUSDT -> BTC)
        const coin = symbol.replace(normalizedQuoteCurrency, '');
        coinPrices[coin] = price;
        console.log(`💰 [useMarketPrices.ts] Converted ${symbol} -> ${coin}: $${price.toLocaleString()}`);
        
        // Tikriname ar simbolis baigiasi su teisingu quoteCurrency
        if (!symbol.endsWith(normalizedQuoteCurrency)) {
          console.warn(`⚠️ [useMarketPrices.ts] WARNING: Symbol ${symbol} does not end with ${normalizedQuoteCurrency}`);
          console.warn(`⚠️ [useMarketPrices.ts] This may indicate a mismatch between symbol and quoteCurrency`);
        }
      });
      
      console.log(`✅ [useMarketPrices.ts] Final coin prices:`, JSON.stringify(coinPrices, null, 2));
      console.log(`✅ [useMarketPrices.ts] Total coins with prices: ${Object.keys(coinPrices).length}`);

      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.11: Atnaujinama būsena');
      // Only update state if component is still mounted
      if (mountedRef.current) {
        setPrices(coinPrices);
        setLastUpdated(new Date());
        setError(null);
        
        console.log(`✅ [useMarketPrices.ts] State updated with ${Object.keys(coinPrices).length} coin prices`);
      }

      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.12: Tikrinamos dalinės klaidos');
      // Handle partial errors (some symbols failed, some succeeded)
      if (data.errors && Object.keys(data.errors).length > 0) {
        console.warn(`⚠️ [useMarketPrices.ts] Some symbols failed:`, JSON.stringify(data.errors, null, 2));
        
        // Show specific warnings for failed coins
        const failedCoins = Object.keys(data.errors).map(symbol => {
          const coin = symbol.replace(normalizedQuoteCurrency, '');
          return coin;
        });
        
        if (failedCoins.length > 0) {
          const failedCoinsStr = failedCoins.join(', ');
          toast.warning(`Some trading pairs not supported: ${failedCoinsStr}/${quoteCurrency}`, {
            duration: 5000
          });
        }
      }

      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.13: fetchMarketPrices sėkmingai baigta');
    } catch (err: any) {
      console.error('[useMarketPrices.ts] SUB-BLOKAS 3.2.14: Klaidos apdorojimas');
      console.error('❌ [useMarketPrices.ts] Error details:', {
        message: err.message,
        stack: err.stack,
        coinSymbols,
        quoteCurrency,
        exchange
      });
      
      const errorMessage = err.message || 'Failed to fetch market prices';
      
      if (mountedRef.current) {
        setError(errorMessage);
        setPrices({});
        
        // Show user-friendly error messages
        if (errorMessage.includes('not supported')) {
          toast.error('Selected quote currency is not supported for these coins on Binance Spot.');
        } else if (errorMessage.includes('Invalid symbol') || errorMessage.includes('not found')) {
          toast.error('Some selected coins are not available for trading with the chosen quote currency.');
        } else if (errorMessage.includes('Rate limit')) {
          toast.error('Too many requests. Please wait a moment and try again.');
        } else if (errorMessage.includes('No prices found')) {
          toast.error('No market prices found for the selected coins. Please check your selections.');
        } else if (errorMessage.includes('temporarily unavailable')) {
          toast.error('Market prices temporarily unavailable. Please try again later.');
        } else if (errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch')) {
          toast.error('Network error. Please check your connection and try again.');
        } else {
          // Only show generic error for truly unexpected errors
          if (!lastUpdated) {
            toast.error(`Failed to fetch market prices: ${errorMessage}`);
          }
        }
      }
    } finally {
      console.log('[useMarketPrices.ts] SUB-BLOKAS 3.2.15: finally blokas');
      if (mountedRef.current) {
        setLoading(false);
        console.log('[useMarketPrices.ts] Loading state set to false');
      }
    }
  };

  console.log('[useMarketPrices.ts] SUB-BLOKAS 3.3: useEffect polling ir initial fetch');
  // Initial fetch and setup polling
  useEffect(() => {
    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.3.1: useEffect pradžia');
    mountedRef.current = true;
    
    // Clear any existing interval
    if (intervalRef.current) {
      console.log('[useMarketPrices.ts] Clearing existing interval');
      clearInterval(intervalRef.current);
    }

    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.3.2: Pirmoji duomenų užklausa');
    // Fetch immediately
    fetchMarketPrices();

    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.3.3: Nustatomas polling intervalas');
    // Set up polling if we have coins to track
    if (coinSymbols.length > 0 && quoteCurrency) {
      console.log(`⏰ [useMarketPrices.ts] Setting up polling every ${pollInterval}ms`);
      
      intervalRef.current = setInterval(() => {
        if (mountedRef.current) {
          console.log('[useMarketPrices.ts] Polling triggered at:', new Date().toISOString());
          fetchMarketPrices();
        }
      }, pollInterval);
    } else {
      console.log('[useMarketPrices.ts] Skipping polling - no coins or quoteCurrency');
    }

    console.log('[useMarketPrices.ts] SUB-BLOKAS 3.3.4: useEffect cleanup funkcija');
    // Cleanup function
    return () => {
      console.log('[useMarketPrices.ts] useEffect cleanup');
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [coinSymbols.join(','), quoteCurrency, exchange, pollInterval]);

  console.log('[useMarketPrices.ts] SUB-BLOKAS 3.4: Papildomas cleanup useEffect');
  // Cleanup on unmount
  useEffect(() => {
    console.log('[useMarketPrices.ts] Cleanup useEffect pradžia');
    return () => {
      console.log('[useMarketPrices.ts] Cleanup useEffect vykdomas');
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  console.log('[useMarketPrices.ts] SUB-BLOKAS 3.5: Manual refetch funkcija');
  // Manual refetch function
  const refetch = () => {
    console.log(`🔄 [useMarketPrices.ts] Manual refetch triggered at:`, new Date().toISOString());
    console.log(`🔄 [useMarketPrices.ts] Current params:`, {
      coinSymbols,
      quoteCurrency,
      exchange,
      pollInterval
    });
    fetchMarketPrices();
  };

  console.log('[useMarketPrices.ts] SUB-BLOKAS 3.6: Hook grąžinimas');
  return {
    prices,
    loading,
    error,
    lastUpdated,
    refetch
  };
};

// ============================================
// BLOKAS 4: DEFAULT EXPORT
// ============================================
console.log('[useMarketPrices.ts] BLOKAS 4: Default export');
export default useMarketPrices;

// ============================================
// BLOKAS 5: FAILO PABAIGA
// ============================================
console.log('[useMarketPrices.ts] BLOKAS 5: Failo pabaiga');
console.log('[useMarketPrices.ts] ✅ useMarketPrices.ts failas sėkmingai užkrautas');